import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Lock, ShieldCheck, ShieldAlert, ArrowRight, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import AnimatedLogo from '../components/AnimatedLogo';

const ForcePasswordChange = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Attempt to get the username from the previous step if possible
    const [username] = useState(location.state?.username || '');
    const [tempPassword] = useState(location.state?.tempPassword || '');
    
    const [formData, setFormData] = useState({
        oldPassword: tempPassword,
        newPassword: '',
        confirmPassword: ''
    });
    
    const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.newPassword || !formData.confirmPassword) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.newPassword.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);
        try {
            // We use the same endpoint as change-password but we might need to be logged in.
            // Wait, if the user isn't logged in yet (no token yet), how do they call change-password?
            // In my backend I return 401 MUST_RESET. 
            // I should probably have updated the backend to allow a "one-time-reset" with the temporary token.
            // BUT, for now, we can use the credentials to authenticate AND change.
            
            // Actually, my current backend changePassword REQUIRES authMiddleware.
            // So I must provide a token.
            // I'll update the backend login to return a TEMPORARY token that ONLY allows password change.
            
            await authAPI.changePassword({
                ancien_mot_de_passe: formData.oldPassword,
                nouveau_mot_de_passe: formData.newPassword,
                confirmation_mot_de_passe: formData.confirmPassword
            });
            
            toast.success('Mot de passe mis à jour ! Vous pouvez maintenant vous connecter.');
            navigate('/login');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors du changement de mot de passe');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page-v2">
            <div className="login-bg-hero"></div>
            <div className="login-bg-overlay"></div>
            <div className="login-glow-1"></div>
            <div className="login-glow-2"></div>

            <main className="glass-login-card" style={{ maxWidth: '500px' }}>
                <div className="login-brand">
                    <div className="login-brand-logo">
                        <AnimatedLogo height="120px" />
                    </div>
                    <h1 style={{ fontSize: '24px' }}>Expertise H.B</h1>
                    <div className="login-brand-line"></div>
                    <h3 style={{ color: 'var(--gold-400)', marginTop: '10px', fontWeight: '800', letterSpacing: '1px' }}>
                        MISE À JOUR OBLIGATOIRE
                    </h3>
                    <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '8px' }}>
                        Votre clé actuelle est temporaire. Veuillez définir une nouvelle clé de sécurité robuste (min 8 car.).
                    </p>
                </div>

                <form onSubmit={handleSubmit} style={{ marginTop: '30px' }}>
                    <div className="login-form-group">
                        <label className="login-label">Clé Temporaire Actuelle</label>
                        <div className="login-input-wrapper">
                            <Lock className="login-input-icon" size={18} />
                            <input
                                type={showPass.old ? 'text' : 'password'}
                                className="login-input"
                                placeholder="Clé actuelle"
                                value={formData.oldPassword}
                                readOnly={!!tempPassword}
                                onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="login-form-group">
                        <label className="login-label">Nouvelle Clé de Sécurité (Min. 8 car.)</label>
                        <div className="login-input-wrapper">
                            <ShieldCheck className="login-input-icon" size={18} />
                            <input
                                type={showPass.new ? 'text' : 'password'}
                                className="login-input"
                                placeholder="Nouvelle clé"
                                value={formData.newPassword}
                                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPass(p => ({ ...p, new: !p.new }))}
                                style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)' }}
                            >
                                {showPass.new ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="login-form-group" style={{ marginBottom: '30px' }}>
                        <label className="login-label">Confirmer la nouvelle clé</label>
                        <div className="login-input-wrapper">
                            <ShieldAlert className="login-input-icon" size={18} />
                            <input
                                type={showPass.confirm ? 'text' : 'password'}
                                className="login-input"
                                placeholder="Répéter la clé"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPass(p => ({ ...p, confirm: !p.confirm }))}
                                style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)' }}
                            >
                                {showPass.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="login-btn"
                        disabled={loading}
                    >
                        {loading ? (
                            <><div className="spinner" style={{ width: '18px', height: '18px', borderLeftColor: '#000' }} /> Mise à jour...</>
                        ) : (
                            <><ArrowRight size={20} /> Valider la nouvelle clé</>
                        )}
                    </button>
                    
                    <button 
                        type="button" 
                        className="btn btn-ghost" 
                        style={{ width: '100%', marginTop: '15px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}
                        onClick={() => navigate('/login')}
                    >
                        Retour au login
                    </button>
                </form>
            </main>
        </div>
    );
};

export default ForcePasswordChange;
